import { Check, Loader2, Pencil, PenLine, Phone, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  HipaaESignRequestModal,
  HipaaESignStatusBadge,
  HipaaReminderModal,
  HipaaVoidConfirmDialog,
  useHipaaESignStatus,
} from '@/components/hipaa-esign';
import { Button } from '@/components/ui/catalyst/button';
import { Text } from '@/components/ui/catalyst/text';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import type {
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import type { MedicalProvider } from '@/services/types/treatment-types';
import { getHipaaSentDate } from '@/types/treatment-record-request-extended';

import { useRequestPage } from '../contexts/RequestPageContext';
import { SelectDestinationModal } from '../modals/SelectDestinationModal';
import { getPrioritizedPhoneHandles } from '../modals/utils/phoneHandleUtils';

interface RequestDetailsProps {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  matterId: string;
  providerId: string;
  requestId: string;
  onUpdate: () => void;
}

const CLEAR_PHONE_OPTION = '__NONE__';

type PhoneOption = {
  key: string;
  value: string;
  label: string;
  badges?: string[];
  description?: string;
};

interface SelectedSummary {
  triggerLabel: string;
  detail?: string;
  description?: string;
  badges?: string[];
}

// ============================================================================
// Submit Request Section
// ============================================================================

interface SubmitRequestSectionProps {
  request: TreatmentRecordRequestSchema;
  matterId: string;
  providerId: string;
  requestId: string;
  onUpdate: () => void;
}

const SubmitRequestSection = ({
  request,
  matterId,
  providerId,
  requestId,
  onUpdate,
}: SubmitRequestSectionProps) => {
  const {
    packageData,
    validationStatus,
    hasMissingSignature,
    hasSubmittedRequest,
  } = useRequestPage();

  const [submitHipaaPackage, { isLoading: isSubmitting }] =
    treatmentRecordRequestService.useSubmitHipaaPackageMutation();
  const [changeStatus] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChangeStatusCreateMutation();

  // Determine channel type and value
  const channelType = request.channel?.type;
  const channelValue = useMemo(() => {
    if (!request.channel) return null;
    switch (request.channel.type) {
      case 'fax':
        return request.channel.fax;
      case 'email':
        return request.channel.email;
      case 'mail':
        return request.channel.address;
      default:
        return null;
    }
  }, [request.channel]);

  // Check package status
  const isPackagePublished = packageData?.status === 'published';
  const isPackageReady = packageData?.status === 'ready' || isPackagePublished;
  const isPackageGenerating = packageData?.status === 'generating';

  // Build list of missing items
  // If package is already published/approved, skip field and signature validation
  const missingItems = useMemo(() => {
    const items: string[] = [];

    // Always require channel info
    if (!channelType) {
      items.push('request method');
    } else if (!channelValue) {
      switch (channelType) {
        case 'fax':
          items.push('fax number');
          break;
        case 'email':
          items.push('email address');
          break;
        case 'mail':
          items.push('mailing address');
          break;
        default:
          items.push('contact info');
      }
    }

    // Only check field/signature validation if package is not yet published
    if (!isPackagePublished) {
      if (validationStatus.hasMissingFields) {
        items.push(
          `${validationStatus.missingFieldsCount} package field${validationStatus.missingFieldsCount !== 1 ? 's' : ''}`,
        );
      }
      if (hasMissingSignature) {
        items.push('HIPAA signature');
      }
      if (!isPackageReady && !isPackageGenerating) {
        items.push('approved package');
      }
    }

    return items;
  }, [
    channelType,
    channelValue,
    validationStatus.hasMissingFields,
    validationStatus.missingFieldsCount,
    hasMissingSignature,
    isPackageReady,
    isPackagePublished,
    isPackageGenerating,
  ]);

  const isReady = missingItems.length === 0 && !isPackageGenerating;

  const getChannelLabel = (type: string | undefined) => {
    switch (type) {
      case 'fax':
        return 'Fax';
      case 'email':
        return 'Email';
      case 'mail':
        return 'Mail';
      default:
        return 'Submit';
    }
  };

  const getSubmitButtonLabel = (type: string | undefined) => {
    switch (type) {
      case 'fax':
        return 'Submit Fax';
      case 'email':
        return 'Submit Email';
      case 'mail':
        return 'Submit Mail';
      default:
        return 'Submit Request';
    }
  };

  // Format submission date
  const formatSubmissionDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleSubmit = async () => {
    if (!channelType) {
      toast.error('Please set a request method first');
      return;
    }

    try {
      await submitHipaaPackage({
        matterId,
        providerId,
        requestId,
        submitHipaaPackageSchema: {
          channel_type: channelType,
        },
      }).unwrap();

      await changeStatus({
        matterId,
        providerId,
        requestId,
        changeTreatmentRecordRequestStatusSchema: {
          new_status: 'request_submitted',
        },
      }).unwrap();

      toast.success(
        `Request submitted via ${getChannelLabel(channelType).toLowerCase()}`,
      );
      onUpdate();
    } catch (error) {
      const apiError = error as { data?: { detail?: string } };
      toast.error(apiError?.data?.detail || 'Failed to submit request');
    }
  };

  // Don't show if no channel is configured at all
  if (!request.channel) {
    return null;
  }

  // STATE: Already submitted
  if (hasSubmittedRequest) {
    // Only show timestamp if we have accurate transmission data (fax/email)
    const submittedAt = getHipaaSentDate(request);
    return (
      <div className="pb-3">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-green-600" />
          <div>
            <Text className="text-sm font-medium text-zinc-700">
              Sent via {getChannelLabel(channelType)}
            </Text>
            {submittedAt && (
              <Text className="text-xs text-zinc-500">
                {formatSubmissionDate(submittedAt)}
              </Text>
            )}
          </div>
        </div>
      </div>
    );
  }

  // STATE: Generating package
  if (isPackageGenerating) {
    return (
      <div className="pb-5">
        <Button color="zinc" className="w-full" disabled>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Package generating...
        </Button>
      </div>
    );
  }

  // STATE: Ready to submit
  if (isReady) {
    return (
      <div className="pb-5">
        <Button
          color="indigo-steel"
          className="w-full"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            getSubmitButtonLabel(channelType)
          )}
        </Button>
      </div>
    );
  }

  // STATE: Not ready
  const buttonLabel = getSubmitButtonLabel(channelType);

  return (
    <div className="pb-5">
      <button
        type="button"
        className="w-full rounded-[14px] bg-zinc-400 px-4 py-3 text-base font-semibold text-white opacity-55 cursor-not-allowed"
        aria-disabled="true"
      >
        {buttonLabel}
      </button>
    </div>
  );
};

// ============================================================================
// Request Details Component
// ============================================================================

export const RequestDetails = ({
  request,
  provider,
  matterId,
  providerId,
  requestId,
  onUpdate,
}: RequestDetailsProps) => {
  const { packageData, matter, primaryClient } = useRequestPage();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [tempPhoneValue, setTempPhoneValue] = useState('');
  const [isEditingChannel, setIsEditingChannel] = useState(false);
  const [tempChannelValue, setTempChannelValue] = useState('');
  const [showESignModal, setShowESignModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);

  // E-sign status hook
  const {
    status: eSignStatus,
    hasRequest: hasESignRequest,
    isPending: isESignPending,
    isVoided: isESignVoided,
    refreshStatus,
    isRefreshing,
    sendReminder,
    isSendingReminder,
    sendRequest: sendESignRequest,
    isSending: isSendingESign,
    voidRequest,
    isVoiding,
    canVoid,
  } = useHipaaESignStatus({
    matterId,
    skip: !matterId,
    refreshOnMount: true,
  });

  // Client contact permission
  const canContactClient = matter?.contact_client_directly !== false;

  // Build client name for e-sign modal
  const clientName = primaryClient
    ? primaryClient.name?.trim() ||
      `${primaryClient.first_name || ''} ${primaryClient.last_name || ''}`.trim()
    : undefined;

  const [updateChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();

  const providerRefId = provider.provider_contact_ref_id || null;

  const submittedStatuses: TreatmentRecordRequestStatus[] = [
    'request_submitted',
    'request_received',
    'awaiting_invoice',
    'pending_payment',
    'records_pending',
    'records_delivered',
    'records_ingested',
    'rejected',
    'cancelled',
  ];

  const hasSubmittedRequest =
    !!request.status && submittedStatuses.includes(request.status);

  const canEditChannel = !hasSubmittedRequest;

  // Get prioritized legacy phone handles from provider (with safety checks)
  const legacyPhoneHandles = useMemo(() => {
    if (!provider || !provider.handles) {
      return [];
    }
    try {
      return getPrioritizedPhoneHandles(provider.handles);
    } catch (error) {
      console.error('Error getting prioritized phone handles:', error);
      return [];
    }
  }, [provider]);

  const { units } = useProviderDirectory({
    providerRefId,
    skip: !providerRefId,
  });

  const linkedDirectoryHandleId =
    request.channel?.provider_contact_unit_handle_id ||
    packageData?.provider_contact_unit_handle_id ||
    null;

  const directoryPhoneOptions = useMemo<PhoneOption[]>(() => {
    if (!linkedDirectoryHandleId || !units || units.length === 0) {
      return [];
    }

    const handleId = String(linkedDirectoryHandleId);

    const owningUnit = units.find((unit) =>
      unit.handles?.some((handle) => String(handle.id) === handleId),
    );

    if (!owningUnit?.handles || owningUnit.handles.length === 0) {
      return [];
    }

    const options: PhoneOption[] = [];

    owningUnit.handles
      ?.filter(
        (handle) =>
          handle.handle?.handle_type === 'PHONE' && handle.handle?.value,
      )
      .forEach((handle) => {
        const option: PhoneOption = {
          key: `directory-${handle.id}`,
          value: handle.handle?.value || '',
          label: handle.handle?.name || owningUnit.name || 'Phone',
          description: owningUnit.name || undefined,
          badges: ['Directory', ...(handle.is_preferred ? ['Preferred'] : [])],
        };

        if (String(handle.id) === handleId) {
          options.unshift(option);
        } else {
          options.push(option);
        }
      });

    return options;
  }, [linkedDirectoryHandleId, units]);

  const legacyPhoneOptions = useMemo<PhoneOption[]>(() => {
    return legacyPhoneHandles
      .filter((handle) => handle?.value)
      .map((handle, idx) => ({
        key: `legacy-${handle.value}-${idx}`,
        value: handle.value as string,
        label: handle.name || 'Phone',
        badges: handle.isPriority ? ['Records/Billing'] : undefined,
      }));
  }, [legacyPhoneHandles]);

  const groupedPhoneOptions = useMemo(() => {
    const seen = new Set<string>();
    const groups: { label: string; options: PhoneOption[] }[] = [];

    const filterUnique = (options: PhoneOption[]) =>
      options.filter((option) => {
        if (!option.value) return false;
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });

    const directoryOptions = filterUnique(directoryPhoneOptions);
    if (directoryOptions.length > 0) {
      groups.push({ label: 'Directory Contacts', options: directoryOptions });
    }

    const legacyOptions = filterUnique(legacyPhoneOptions);
    if (legacyOptions.length > 0) {
      groups.push({ label: 'Legacy Contacts', options: legacyOptions });
    }

    return groups;
  }, [directoryPhoneOptions, legacyPhoneOptions]);

  const hasPhoneOptions = groupedPhoneOptions.some(
    (group) => group.options.length > 0,
  );

  const availablePhoneValues = useMemo(() => {
    const values = new Set<string>();
    groupedPhoneOptions.forEach((group) => {
      group.options.forEach((option) => {
        if (option.value) {
          values.add(option.value);
        }
      });
    });
    return values;
  }, [groupedPhoneOptions]);

  const optionLookup = useMemo(() => {
    const map = new Map<string, PhoneOption>();
    groupedPhoneOptions.forEach((group) => {
      group.options.forEach((option) => {
        if (option.value) {
          map.set(option.value, option);
        }
      });
    });
    return map;
  }, [groupedPhoneOptions]);

  // Check if current phone number exists in available options
  const currentPhoneInOptions = useMemo(() => {
    const currentPhone = request.channel?.phone_number;
    if (!currentPhone) return true;
    return availablePhoneValues.has(currentPhone);
  }, [request.channel?.phone_number, availablePhoneValues]);

  const [selectValue, setSelectValue] = useState<string | undefined>(
    request.channel?.phone_number ||
      (!hasSubmittedRequest ? CLEAR_PHONE_OPTION : undefined),
  );

  useEffect(() => {
    setSelectValue(
      request.channel?.phone_number ||
        (!hasSubmittedRequest ? CLEAR_PHONE_OPTION : undefined),
    );
  }, [request.channel?.phone_number, hasSubmittedRequest]);

  useEffect(() => {
    if (hasSubmittedRequest && selectValue === CLEAR_PHONE_OPTION) {
      const fallbackValue =
        request.channel?.phone_number ||
        groupedPhoneOptions[0]?.options[0]?.value ||
        undefined;
      setSelectValue(fallbackValue);
    }
  }, [
    hasSubmittedRequest,
    selectValue,
    request.channel?.phone_number,
    groupedPhoneOptions,
  ]);

  const selectedSummary = useMemo<SelectedSummary | null>(() => {
    if (!selectValue) {
      return null;
    }

    if (selectValue === CLEAR_PHONE_OPTION) {
      if (hasSubmittedRequest) return null;
      return {
        triggerLabel: 'No phone number',
        detail: 'Contact will omit a phone',
        badges: ['None'],
      };
    }

    const option = optionLookup.get(selectValue);
    if (option) {
      return {
        triggerLabel: option.label,
        detail: option.value,
        description: option.description,
        badges: option.badges,
      };
    }

    if (!currentPhoneInOptions && request.channel?.phone_number) {
      return {
        triggerLabel: 'Current Number',
        detail: request.channel.phone_number,
        badges: ['Custom'],
      };
    }

    return null;
  }, [
    selectValue,
    hasSubmittedRequest,
    optionLookup,
    currentPhoneInOptions,
    request.channel?.phone_number,
  ]);

  // Helper to get user-friendly error message from API errors
  const getChannelUpdateErrorMessage = (error: unknown, fallback: string) => {
    const apiError = error as { data?: { detail?: string } };
    if (apiError?.data?.detail?.includes('WorkflowAlreadyStarted')) {
      return 'Request is being processed. Please wait a moment and try again.';
    }
    return fallback;
  };

  const handleUpdatePhoneNumber = async (phoneNumber: string) => {
    if (!request.channel?.id) {
      toast.error('No channel found to update');
      return;
    }

    // Handle special "__NONE__" value for clearing phone number
    const actualPhoneNumber =
      phoneNumber === CLEAR_PHONE_OPTION ? '' : phoneNumber;

    try {
      await updateChannel({
        matterId,
        providerId,
        requestId,
        channelId: request.channel.id,
        updateTreatmentRecordRequestChannelSchema: {
          phone_number: actualPhoneNumber || null,
        },
      }).unwrap();
      toast.success('Contact phone number updated');
      onUpdate();
    } catch (error) {
      toast.error(
        getChannelUpdateErrorMessage(error, 'Failed to update phone number'),
      );
    }
  };

  const handleStartEditPhone = () => {
    setTempPhoneValue(request.channel?.phone_number || '');
    setIsEditingPhone(true);
  };

  const handleCancelEditPhone = () => {
    setIsEditingPhone(false);
    setTempPhoneValue('');
  };

  const handleSavePhoneNumber = async () => {
    await handleUpdatePhoneNumber(tempPhoneValue);
    setIsEditingPhone(false);
  };

  // Channel value editing handlers
  const handleStartEditChannel = () => {
    const currentValue = (() => {
      switch (request.channel?.type) {
        case 'fax':
          return request.channel.fax || '';
        case 'email':
          return request.channel.email || '';
        case 'mail':
          return request.channel.address || '';
        default:
          return '';
      }
    })();
    setTempChannelValue(currentValue);
    setIsEditingChannel(true);
  };

  const handleCancelEditChannel = () => {
    setIsEditingChannel(false);
    setTempChannelValue('');
  };

  const handleSaveChannelValue = async () => {
    if (!request.channel?.id) {
      toast.error('No channel found to update');
      return;
    }

    try {
      const updateData: Record<string, string | null> = {};
      switch (request.channel.type) {
        case 'fax':
          updateData.fax = tempChannelValue || null;
          break;
        case 'email':
          updateData.email = tempChannelValue || null;
          break;
        case 'mail':
          updateData.address = tempChannelValue || null;
          break;
        default:
          break;
      }

      await updateChannel({
        matterId,
        providerId,
        requestId,
        channelId: request.channel.id,
        updateTreatmentRecordRequestChannelSchema: updateData,
      }).unwrap();
      toast.success('Contact details updated');
      onUpdate();
      setIsEditingChannel(false);
    } catch (error) {
      toast.error(
        getChannelUpdateErrorMessage(error, 'Failed to update contact details'),
      );
    }
  };

  const getChannelPlaceholder = () => {
    switch (request.channel?.type) {
      case 'fax':
        return 'Enter fax number';
      case 'email':
        return 'Enter email address';
      case 'mail':
        return 'Enter mailing address';
      default:
        return 'Enter value';
    }
  };

  const getChannelInputType = () => {
    switch (request.channel?.type) {
      case 'email':
        return 'email';
      case 'fax':
        return 'tel';
      default:
        return 'text';
    }
  };

  // Format channel information
  const getChannelInfo = () => {
    if (!request.channel) return 'Not specified';
    const channelType = request.channel.type;
    return channelType.charAt(0).toUpperCase() + channelType.slice(1);
  };

  const handlePhoneSelectChange = (value: string) => {
    // Prevent unnecessary API calls when selecting the same value
    const currentPhoneNumber = request.channel?.phone_number || '';
    const newPhoneNumber = value === CLEAR_PHONE_OPTION ? '' : value;

    if (currentPhoneNumber === newPhoneNumber) {
      // Value unchanged, just update local state
      setSelectValue(value);
      return;
    }

    setSelectValue(value);
    handleUpdatePhoneNumber(value);
  };

  // Get channel value (fax number, email, etc.)
  const getChannelValue = () => {
    if (!request.channel) return null;
    switch (request.channel.type) {
      case 'fax':
        return request.channel.fax;
      case 'email':
        return request.channel.email;
      case 'mail':
        return request.channel.address;
      case 'portal':
        return request.channel.portal_provider_id
          ? `Portal: ${request.channel.portal_provider_id}`
          : null;
      default:
        return null;
    }
  };

  const channelValue = getChannelValue();
  const channelMissing = request.channel && !channelValue;
  const formatProvidedRecord = (record: string) =>
    record
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((part, index) => {
        const normalized = part.toLowerCase();

        if (normalized === 'er') return 'ER';
        if (normalized === 'hipaa') return 'HIPAA';
        if (normalized === 'id') return 'ID';
        if (normalized === 'dob') return 'DOB';
        if (normalized === 'ssn') return 'SSN';
        if (
          index > 0 &&
          ['of', 'and', 'or', 'to', 'via', 'for'].includes(normalized)
        ) {
          return normalized;
        }

        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
      })
      .join(' ');

  return (
    <div className="space-y-1">
      {/* Submit Request Section */}
      <SubmitRequestSection
        request={request}
        matterId={matterId}
        providerId={providerId}
        requestId={requestId}
        onUpdate={onUpdate}
      />

      {/* Records Requested */}
      <div className="border-t border-zinc-100 py-5">
        <Text className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-zinc-400">
          Records Requested
        </Text>
        <Text className="text-[15px] leading-8 text-zinc-800">
          {request.provided_records?.length
            ? request.provided_records.map(formatProvidedRecord).join(' + ')
            : 'Not specified'}
        </Text>
      </div>

      {/* Notes */}
      {request.metadata?.notes && (
        <div className="border-t border-zinc-100 py-5">
          <Text className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-zinc-400">
            Notes
          </Text>
          <Text className="whitespace-pre-wrap text-[15px] leading-7 text-zinc-800">
            {request.metadata.notes}
          </Text>
        </div>
      )}

      {/* Request Method */}
      <div className="border-t border-zinc-100 py-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <Text className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-zinc-400">
              Request Method
            </Text>
            {isEditingChannel ? (
              <div className="flex items-center gap-2 mt-1">
                <Text className="text-sm text-zinc-800 flex-shrink-0">
                  {getChannelInfo()}
                </Text>
                <input
                  type={getChannelInputType()}
                  value={tempChannelValue}
                  onChange={(e) => setTempChannelValue(e.target.value)}
                  placeholder={getChannelPlaceholder()}
                  className="h-7 px-2 text-sm border-b border-zinc-300 focus:border-zinc-500 focus:outline-none bg-transparent max-w-[200px]"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveChannelValue();
                    else if (e.key === 'Escape') handleCancelEditChannel();
                  }}
                />
                <button
                  type="button"
                  onClick={handleSaveChannelValue}
                  className="p-1 text-green-600 hover:text-green-700"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleCancelEditChannel}
                  className="p-1 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 group min-w-0">
                  <Text className="flex-shrink-0 text-[15px] text-zinc-800">
                    {getChannelInfo()}
                  </Text>
                  {channelValue ? (
                    <Text className="truncate text-[15px] text-zinc-500">
                      {channelValue}
                    </Text>
                  ) : (
                    <span className="text-[15px] italic text-zinc-400">
                      Not provided
                    </span>
                  )}
                  {/* Inline edit only for fax/email - mail uses modal for directory selection */}
                  {canEditChannel &&
                    request.channel?.type &&
                    ['fax', 'email'].includes(request.channel.type) && (
                      <button
                        type="button"
                        onClick={handleStartEditChannel}
                        className={`ml-2 text-zinc-400 hover:text-zinc-600 transition-all flex items-center gap-1 text-xs flex-shrink-0 ${
                          channelMissing
                            ? ''
                            : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                </div>
                {/* Only show instructions if they're actual user instructions, not auto-generated */}
                {request.channel?.instructions &&
                  !request.channel.instructions.startsWith(
                    'Selected from Provider Directory',
                  ) && (
                    <Text className="mt-1 text-xs italic text-zinc-400">
                      {request.channel.instructions}
                    </Text>
                  )}
              </>
            )}
          </div>
          {canEditChannel && !isEditingChannel && (
            <button
              type="button"
              onClick={() => setIsEditModalOpen(true)}
              className="ml-2 flex-shrink-0 text-sm text-zinc-400 transition-colors hover:text-zinc-600"
              title="Change request method"
            >
              Change
            </button>
          )}
        </div>
      </div>

      {/* Contact Phone */}
      <div className="border-t border-zinc-100 py-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Text className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-zinc-400">
              Contact Phone
            </Text>
            {hasPhoneOptions ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Select
                  value={selectValue}
                  onValueChange={handlePhoneSelectChange}
                >
                  <SelectTrigger className="h-11 w-full bg-white text-sm border-zinc-200">
                    <SelectValue>
                      {selectedSummary?.detail ||
                        selectedSummary?.triggerLabel ||
                        'Select phone'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent
                    className="max-h-[300px]"
                    position="popper"
                    sideOffset={4}
                  >
                    {!hasSubmittedRequest && (
                      <SelectItem value={CLEAR_PHONE_OPTION} className="py-2">
                        <span className="text-sm text-gray-600">
                          No phone number
                        </span>
                      </SelectItem>
                    )}
                    {!currentPhoneInOptions &&
                      request.channel?.phone_number && (
                        <SelectItem
                          value={request.channel.phone_number}
                          className="py-2"
                        >
                          <div className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 text-zinc-400" />
                            <span className="text-sm">
                              {request.channel.phone_number}
                            </span>
                            <span className="text-[10px] text-zinc-400">
                              Custom
                            </span>
                          </div>
                        </SelectItem>
                      )}
                    {groupedPhoneOptions.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel className="px-2 py-1 text-[10px] font-medium uppercase text-zinc-400">
                          {group.label}
                        </SelectLabel>
                        {group.options.map((option) => (
                          <SelectItem
                            key={option.key}
                            value={option.value}
                            className="py-2"
                          >
                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5 text-zinc-400" />
                              <span className="text-sm">{option.value}</span>
                              {option.badges?.map((badge) => (
                                <span
                                  key={`${option.key}-${badge}`}
                                  className="text-[10px] text-zinc-400"
                                >
                                  {badge}
                                </span>
                              ))}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : isEditingPhone ? (
              <div className="flex items-center gap-2">
                <input
                  type="tel"
                  value={tempPhoneValue}
                  onChange={(e) => setTempPhoneValue(e.target.value)}
                  placeholder="Enter phone number"
                  className="h-7 px-2 text-sm border-b border-zinc-300 focus:border-zinc-500 focus:outline-none bg-transparent max-w-[200px]"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSavePhoneNumber();
                    else if (e.key === 'Escape') handleCancelEditPhone();
                  }}
                />
                <button
                  type="button"
                  onClick={handleSavePhoneNumber}
                  className="p-1 text-green-600 hover:text-green-700"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleCancelEditPhone}
                  className="p-1 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <Text className="text-sm text-zinc-800">
                  {request.channel?.phone_number || (
                    <span className="text-zinc-400 italic">Not provided</span>
                  )}
                </Text>
                <button
                  type="button"
                  onClick={handleStartEditPhone}
                  className={`text-zinc-400 hover:text-zinc-600 transition-all flex items-center gap-1 text-xs ${
                    request.channel?.phone_number
                      ? 'opacity-0 group-hover:opacity-100'
                      : ''
                  }`}
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Select Destination Modal */}
      <SelectDestinationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        request={request}
        providerId={providerId}
        matterId={matterId}
        provider={provider}
        onSuccess={onUpdate}
      />

      {/* HIPAA E-Signature */}
      <div className="border-t border-zinc-100 py-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Text className="text-xs font-medium uppercase tracking-[0.12em] text-zinc-400">
                HIPAA E-Signature
              </Text>
              {hasESignRequest && eSignStatus && (
                <HipaaESignStatusBadge status={eSignStatus.status} size="sm" />
              )}
            </div>
            <Text className="text-sm leading-7 text-zinc-500">
              {!hasESignRequest
                ? 'Request client signature electronically'
                : eSignStatus?.status === 'SIGNED' ||
                    eSignStatus?.status === 'COMPLETED'
                  ? 'Client has signed the HIPAA authorization'
                  : eSignStatus?.status === 'SENT'
                    ? 'Waiting for client to sign'
                    : eSignStatus?.status === 'VOIDED'
                      ? 'Previous request was cancelled'
                      : 'Processing signature request'}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            {!hasESignRequest ? (
              <button
                type="button"
                onClick={() => setShowESignModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                           bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
              >
                <PenLine className="h-3.5 w-3.5" />
                Request
              </button>
            ) : isESignPending ? (
              <>
                <button
                  type="button"
                  onClick={refreshStatus}
                  disabled={isRefreshing}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-600 hover:bg-zinc-100 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? 'Checking...' : 'Refresh'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReminderModal(true)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-600 hover:bg-zinc-100 transition-colors"
                >
                  Remind
                </button>
                {canVoid && (
                  <button
                    type="button"
                    onClick={() => setShowVoidDialog(true)}
                    disabled={isVoiding}
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    Void
                  </button>
                )}
              </>
            ) : isESignVoided ? (
              <button
                type="button"
                onClick={() => setShowESignModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                           bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
              >
                <PenLine className="h-3.5 w-3.5" />
                Resend
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* E-Sign Request Modal */}
      <HipaaESignRequestModal
        isOpen={showESignModal}
        onClose={() => setShowESignModal(false)}
        onSubmit={async (params) => {
          await sendESignRequest(params);
        }}
        isSubmitting={isSendingESign}
        clientName={clientName}
        clientEmail={primaryClient?.email}
        clientPhone={primaryClient?.phone_numbers?.[0]?.phone_number}
        firmName={matter?.firm?.name}
        canContactClient={canContactClient}
        zIndex={Z_INDEX_MODAL_NESTED}
      />

      {/* E-Sign Reminder Modal */}
      <HipaaReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSubmit={async (params) => {
          await sendReminder(params);
        }}
        isSubmitting={isSendingReminder}
        originalEmail={primaryClient?.email}
        clientName={clientName}
        firmName={matter?.firm?.name}
        zIndex={Z_INDEX_MODAL_NESTED}
      />

      {/* Void Confirmation Dialog */}
      <HipaaVoidConfirmDialog
        open={showVoidDialog}
        onOpenChange={setShowVoidDialog}
        onConfirm={voidRequest}
        isVoiding={isVoiding}
        zIndex={Z_INDEX_MODAL_NESTED}
      />
    </div>
  );
};
